import { useCallback, useEffect, useState } from "react";
import { IModules } from "../../../../models/IUser";
import {
  DeviceFieldKey,
  FIELD_VALIDATORS,
} from "../deviceTypeConfig";

export function useModuleFieldState(
  module: IModules | null,
  markMutated: () => void,
) {
  const [editedModule, setEditedModule] = useState<IModules | null>(null);
  const [changedModuleFields, setChangedModuleFields] = useState<Set<string>>(
    new Set(),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (module) {
      setEditedModule({ ...module });
    }
  }, [module]);

  const validateValue = useCallback(
    (name: string, value: unknown): string | null => {
      const validators = FIELD_VALIDATORS[name as DeviceFieldKey];
      if (!validators) return null;
      const strValue = value == null ? "" : String(value);
      for (const validate of validators) {
        const error = validate(strValue);
        if (error) return error;
      }
      return null;
    },
    [],
  );

  const applyValidation = useCallback(
    (name: string, value: unknown) => {
      const error = validateValue(name, value);
      setFieldErrors((prev) => {
        if (error) return { ...prev, [name]: error };
        if (!(name in prev)) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
    },
    [validateValue],
  );

  const trackField = (name: string) => {
    setChangedModuleFields((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    markMutated();
    const { name, value } = e.target;

    setEditedModule((prev) => (prev ? { ...prev, [name]: value } : prev));
    trackField(name);
    applyValidation(name, value);
  };

  const setField = (name: keyof IModules, value: unknown) => {
    markMutated();
    setEditedModule((prev) => (prev ? { ...prev, [name]: value } : prev));
    trackField(name as string);
    applyValidation(name as string, value);
  };

  const validateAll = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!editedModule) return errors;

    for (const name of changedModuleFields) {
      const error = validateValue(name, editedModule[name as keyof IModules]);
      if (error) {
        errors[name] = error;
      }
    }

    setFieldErrors(errors);
    return errors;
  }, [editedModule, changedModuleFields, validateValue]);

  const reset = () => {
    setChangedModuleFields(new Set());
    setFieldErrors({});
  };

  return {
    editedModule,
    changedModuleFields,
    changedModuleFieldKeys: Array.from(changedModuleFields),
    fieldErrors,
    handleChange,
    setField,
    validateAll,
    reset,
  };
}
